import React, { useEffect, useState } from "react";
// import { Button } from "ui/button";
import { Badge } from "ui/badge";
import {
  Truck,
  GripVertical,
  ArrowRight,
  MapPin,
  // Sparkles,
  Trash2,
  LockKeyhole,
  Clock3,
  Sparkles,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  useSensors,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import {
  AssignedTourAtraaf,
  getAssignedTourAtraaf,
  deleteAssignedTourAtraaf,
  TourAtraaf,
  TravelSegment,
  getAtraafRecommendations,
  assignAtraafsBulk,
  AtraafRecommendation,
} from "services/tourApprovalService";
import { VenueApi } from "services/tourService";
import { SaveTourAtraafPayload } from "services/tourApprovalService";
import { Button } from "ui/button";

export type Excursion = {
  id: string;
  name: string;
  time: string;
  type: "mandatory" | "included" | "optional" | "free";
  durationMinutes?: number;
  masterId?: string;
};

export type Activity = {
  id: string;
  name: string;
  description: string;
  badge?: string;
  durationMinutes: number;
  venueId: number;
  venueName: string;
  stops: string[];
  optionalRoute: boolean;
  transportRequired: boolean;
};

export type SidebarCollapsed = {
  transport: boolean;
  mawaid: boolean;
  ziyarat: boolean;
  excursions: boolean;
};

export type ExcursionVenueSchedule = {
  id: string;
  venueId: number | null;
  venuePublicId?: string;
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
};

interface ExcursionsProps {
  tourAtraaf: TourAtraaf[];
  tourPublicId: string;
  selectedApplicants: string[];
  venueSchedules: ExcursionVenueSchedule[];
  venueNames: Record<number, string>;
  travelSegments: TravelSegment[];
  excursionsByDay: Record<number, Excursion[]>;
  isReadOnly: boolean;
  setExcursionsByDay: React.Dispatch<
    React.SetStateAction<Record<number, Excursion[]>>
  >;
  assignedAtraaf: AssignedTourAtraaf[];
  setAssignedAtraaf: React.Dispatch<React.SetStateAction<AssignedTourAtraaf[]>>;
  sidebarCollapsed: SidebarCollapsed;
  setSidebarCollapsed: React.Dispatch<React.SetStateAction<SidebarCollapsed>>;
  venues: VenueApi[];
  saveTourAtraaf: (
    tourPublicId: string,
    payload: SaveTourAtraafPayload,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<any>;
  dropTargetDay: number | null;
  sensors: ReturnType<typeof useSensors>;
  handleExcursionDragEnd: (event: DragEndEvent, day: number) => void;
  handleActivityDragStart: (activity: Activity, category: string) => void;
  handleActivityDragEnd: () => void;
  handleDayDragOver: (e: React.DragEvent, day: number) => void;
  handleDayDragLeave: () => void;
  handleDayDrop: (e: React.DragEvent, day: number) => void;
}

const parseDurationMinutes = (value: string | number | undefined) => {
  if (value === undefined || value === null || value === "") return 0;
  if (typeof value === "number") return Math.round(value * 60);
  const normalized = String(value).trim();
  if (normalized.includes(":")) {
    const [hours = 0, minutes = 0] = normalized.split(":").map(Number);
    return hours * 60 + minutes;
  }
  const numeric = Number(normalized.replace(/[^\d.]/g, ""));
  return Number.isFinite(numeric) ? Math.round(numeric * 60) : 0;
};

const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (!hours) return `${remainder} min`;
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
};

const parseClockMinutes = (value: string) => {
  if (!value) return 0;
  const match = value
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (!match) return 0;
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = match[3]?.toUpperCase();
  if (meridiem === "PM" && hours < 12) hours += 12;
  if (meridiem === "AM" && hours === 12) hours = 0;
  return hours * 60 + minutes;
};

interface SortableExcursionCardProps {
  excursion: {
    id: string;
    name: string;
    time: string;
    type: "mandatory" | "included" | "optional" | "free";
  };
  onRemove: (id: string) => void;
  isReadOnly: boolean;
}

function SortableExcursionCard({
  excursion,
  onRemove,
  isReadOnly,
}: SortableExcursionCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: excursion.id });

  const dragAttributes = isReadOnly ? {} : attributes;
  const dragListeners = isReadOnly ? {} : listeners;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isMandatory = excursion.type === "mandatory";
  const isIncluded = excursion.type === "included";
  const canRemove = !isMandatory && !isIncluded;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-col sm:flex-row sm:items-center gap-2 p-2 bg-white rounded border border-gray-200 hover:border-gray-300 transition-colors"
    >
      <div
        {...dragAttributes}
        {...dragListeners}
        className={
          isReadOnly
            ? "cursor-not-allowed"
            : "cursor-grab active:cursor-grabbing"
        }
      >
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-sm text-gray-900">{excursion.name}</h4>
        <p className="text-xs text-gray-600 mt-0.5">{excursion.time}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {(isMandatory || isIncluded) && (
          <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs px-1.5 py-0">
            Included
          </Badge>
        )}
        {excursion.type === "optional" && (
          <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-xs px-1.5 py-0">
            Optional
          </Badge>
        )}
        {excursion.type === "free" && (
          <Badge className="bg-green-100 text-green-700 border-green-300 text-xs px-1.5 py-0">
            Free
          </Badge>
        )}
        {canRemove && (
          <button
            disabled={isReadOnly}
            onClick={() => onRemove(excursion.id)}
            className={`p-1 rounded transition-colors ${
              isReadOnly
                ? "text-red-400 opacity-50 cursor-not-allowed"
                : "hover:bg-red-50"
            }`}
          >
            <Trash2 className="h-3.5 w-3.5 text-red-600" />
          </button>
        )}
      </div>
    </div>
  );
}

function ActivityDraggable({
  item,
  category,
  children,
  isReadOnly,
}: {
  item: Activity;
  category: string;
  children: React.ReactNode;
  isReadOnly: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `${category}-${item.id}`,
    });

  const dragAttributes = isReadOnly ? {} : attributes;
  const dragListeners = isReadOnly ? {} : listeners;

  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    opacity: isDragging ? 0.65 : 1,
    position: "relative",
    zIndex: isDragging ? 50 : "auto",
    touchAction: "none",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...dragListeners}
      {...dragAttributes}
      className={`touch-none ${
        isReadOnly
          ? "cursor-not-allowed opacity-60"
          : "cursor-grab active:cursor-grabbing"
      }`}
    >
      {children}
    </div>
  );
}

function DayDropZone({
  day,
  children,
}: {
  day: number;
  children: React.ReactNode;
}) {
  const { setNodeRef } = useDroppable({
    id: `day-${day}`,
  });
  return <div ref={setNodeRef}>{children}</div>;
}

function AvailableAtraafDropZone({ children }: { children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({
    id: "available-atraaf",
  });

  return (
    <div
      ref={setNodeRef}
      className={`h-full transition-all ${
        isOver ? "ring-2 ring-purple-500 ring-inset bg-purple-50/40" : ""
      }`}
    >
      {children}
    </div>
  );
}

export default function Excursions({
  tourPublicId,
  saveTourAtraaf,
  selectedApplicants,
  venueSchedules,
  venueNames,
  travelSegments,
  excursionsByDay,
  setExcursionsByDay,
  assignedAtraaf,
  setAssignedAtraaf,
  sidebarCollapsed,
  setSidebarCollapsed,
  dropTargetDay,
  sensors,
  handleExcursionDragEnd,
  tourAtraaf,
  venues,
  isReadOnly,
}: ExcursionsProps) {
  const [atraafOptions, setAtraafOptions] = useState<Activity[]>([]);
  const [loadingAtraaf, setLoadingAtraaf] = useState(true);

  const [atraafRecommendations, setAtraafRecommendations] = useState<
    AtraafRecommendation[]
  >([]);
  const [loadingItineraryRecommendations, setLoadingItineraryRecommendations] =
    useState(true);
  const [recommendingItinerary, setRecommendingItinerary] = useState(false);

  useEffect(() => {
    setAtraafOptions(
      tourAtraaf.map((item) => ({
        id: item.publicId,
        name: item.atraafName,
        description: `${item.venueName} · ${item.durationHours.substring(0, 5)} · ${item.details.length} stops`,
        badge: item.optionalRoute ? "Optional" : "Included",
        durationMinutes: parseDurationMinutes(item.durationHours),
        venueId: item.venueId,
        venueName: item.venueName,
        stops: item.details.map((d) => d.ziyaratName),
        optionalRoute: item.optionalRoute,
        transportRequired: item.transportRequired,
      })),
    );
    setLoadingAtraaf(false);
  }, [tourAtraaf]);

  useEffect(() => {
    const loadAtraafRecommendations = async () => {
      if (!tourPublicId) {
        setAtraafRecommendations([]);
        setLoadingItineraryRecommendations(false);
        return;
      }
      try {
        setLoadingItineraryRecommendations(true);
        const response = await getAtraafRecommendations(tourPublicId);
        if (!response.success) {
          toast.error(
            response.message || "Failed to load Atraaf recommendations.",
          );
          setAtraafRecommendations([]);
          return;
        }
        setAtraafRecommendations(response.data || []);
      } catch (error) {
        toast.error("Failed to load Atraaf recommendations.");
        setAtraafRecommendations([]);
      } finally {
        setLoadingItineraryRecommendations(false);
      }
    };

    loadAtraafRecommendations();
  }, [tourPublicId]);

  const formatDate = (value: string) => {
    if (!value) return "Date pending";
    return new Date(`${value.split("T")[0]}T00:00:00`).toLocaleDateString(
      "en-GB",
      { day: "2-digit", month: "short" },
    );
  };

  const selectedVenues = venueSchedules.filter(
    (schedule): schedule is ExcursionVenueSchedule & { venueId: number } =>
      schedule.venueId !== null,
  );
  const getVenueName = (venueId: number) =>
    venueNames[venueId] || `Venue ${venueId}`;
  const mandatoryTransport = travelSegments.map((segment) => ({
    id: segment.sequence.toString(),
    date: segment.startDate.split("T")[0],
    name: `${segment.segmentType}: ${segment.fromName} → ${segment.toName}`,
    time: `${formatDate(segment.startDate)} · ${segment.startLabel} ${segment.startTime} → ${segment.endLabel} ${segment.endTime}`,
    durationMinutes: Math.max(
      0,
      parseClockMinutes(segment.endTime) - parseClockMinutes(segment.startTime),
    ),
  }));

  const itineraryDays: {
    date: string;
    venueName: string;
    venueId: number | null;
  }[] = [];

  const arrivalSegment = travelSegments.find(
    (x) => x.segmentType === "Arrival",
  );

  const departureSegment = travelSegments.find(
    (x) => x.segmentType === "Departure",
  );

  const itineraryStart = arrivalSegment?.startDate.split("T")[0] ?? "";
  const itineraryEnd = departureSegment?.startDate.split("T")[0] ?? "";

  if (itineraryStart && itineraryEnd) {
    const cursor = new Date(`${itineraryStart}T00:00:00`);
    const end = new Date(`${itineraryEnd}T00:00:00`);
    while (cursor <= end) {
      const date = [
        cursor.getFullYear(),
        String(cursor.getMonth() + 1).padStart(2, "0"),
        String(cursor.getDate()).padStart(2, "0"),
      ].join("-");
      const activeVenue = selectedVenues.find(
        (venue) =>
          venue.fromDate.split("T")[0] <= date &&
          venue.toDate.split("T")[0] >= date,
      );
      itineraryDays.push({
        date,
        venueName: activeVenue ? getVenueName(activeVenue.venueId) : "",
        venueId: activeVenue?.venueId ?? null,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  const getDayContext = (dayNumber: number) => {
    const itineraryDay = itineraryDays[dayNumber - 1];
    if (!itineraryDay) return null;
    const transportMinutes = mandatoryTransport
      .filter((item) => item.date === itineraryDay.date)
      .reduce((total, item) => total + item.durationMinutes, 0);
    const capacityMinutes = Math.max(0, 24 * 60 - transportMinutes);
    const activities = excursionsByDay[dayNumber] || [];
    const usedMinutes = activities.reduce(
      (total, item) => total + (item.durationMinutes || 0),
      0,
    );
    return {
      ...itineraryDay,
      capacityMinutes,
      usedMinutes,
      remainingMinutes: Math.max(0, capacityMinutes - usedMinutes),
    };
  };

  const handleGlobalDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (overId === "available-atraaf") {
      let selectedDay: number | null = null;
      let selectedExcursion: Excursion | null = null;
      for (const [dayKey, activities] of Object.entries(excursionsByDay)) {
        const found = activities.find((activity) => activity.id === activeId);
        if (found) {
          selectedDay = Number(dayKey);
          selectedExcursion = found;
          break;
        }
      }
      if (!selectedExcursion || selectedDay === null) {
        return;
      }
      if (!selectedExcursion.masterId) {
        toast.error("Unable to identify the selected excursion.");
        return;
      }
      const assigned = assignedAtraaf.find(
        (item) => item.atraafPublicId === selectedExcursion?.masterId,
      );
      if (!assigned) {
        toast.error("Assignment not found.");
        return;
      }
      try {
        const response = await deleteAssignedTourAtraaf(assigned.publicId);
        if (!response.success) {
          toast.error(response.message || "Failed to remove excursion.");
          return;
        }
        setExcursionsByDay((prev) => ({
          ...prev,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          [selectedDay!]: (prev[selectedDay!] || []).filter(
            (item) => item.id !== activeId,
          ),
        }));
        setAssignedAtraaf((prev) =>
          prev.filter((item) => item.publicId !== assigned.publicId),
        );
        toast.success(`${selectedExcursion.name} removed from itinerary`);
      } catch (error) {
        console.error("Failed to remove excursion:", error);
        toast.error("Failed to remove excursion.");
      }
      return;
    }
    if (overId.startsWith("day-")) {
      const day = Number(overId.replace("day-", ""));
      const activity = atraafOptions.find(
        (item) => activeId === `atraaf-${item.id}`,
      );
      if (!activity) {
        return;
      }
      const context = getDayContext(day);
      if (!context) return;
      if (context.venueId && activity.venueId !== context.venueId) {
        toast.error(
          `${activity.name} is configured for ${activity.venueName}; Day ${day} is ${context.venueName}`,
        );
        return;
      }
      if (activity.durationMinutes > context.remainingMinutes) {
        toast.error(
          `${activity.name} needs ${formatDuration(
            activity.durationMinutes,
          )}, but only ${formatDuration(
            context.remainingMinutes,
          )} is available on Day ${day}`,
        );
        return;
      }

      const selectedVenue = venues.find((v) => v.venueId === context.venueId);

      if (!selectedVenue?.publicId) {
        toast.error("Venue Public Id not found");
        return;
      }
      const newExcursion: Excursion = {
        id: `atraaf-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 9)}`,
        name: activity.name,
        time: activity.description,
        type: activity.optionalRoute ? "optional" : "included",
        durationMinutes: activity.durationMinutes,
        masterId: activity.id,
      };
      setExcursionsByDay((prev) => ({
        ...prev,
        [day]: [...(prev[day] || []), newExcursion],
      }));
      toast.success(`${activity.name} added`);
      try {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        await saveTourAtraaf(tourPublicId!, {
          venuePublicId: selectedVenue.publicId,
          venueDate: context.date,
          atraafPublicIds: [activity.id],
        });
        try {
          const assigned = await getAssignedTourAtraaf(tourPublicId);
          setAssignedAtraaf(assigned.data.assignments);
        } catch (assignmentError) {
          console.error("Failed to refresh assigned Atraaf:", assignmentError);
        }
      } catch (error) {
        console.error("Failed to save excursion:", error);
        setExcursionsByDay((prev) => ({
          ...prev,
          [day]: (prev[day] || []).filter(
            (item) => item.id !== newExcursion.id,
          ),
        }));
        toast.error(
          `Failed to save ${activity.name}. It was removed from the day.`,
        );
      }
      return;
    }
    const existingDay = Object.entries(excursionsByDay).find(([, activities]) =>
      activities.some((activity) => activity.id === activeId),
    );
    if (!existingDay) return;
    const [dayKey, activities] = existingDay;
    const oldIndex = activities.findIndex(
      (activity) => activity.id === activeId,
    );
    const newIndex = activities.findIndex((activity) => activity.id === overId);
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
      return;
    }
    setExcursionsByDay((prev) => ({
      ...prev,
      [Number(dayKey)]: arrayMove(
        prev[Number(dayKey)] || [],
        oldIndex,
        newIndex,
      ),
    }));
    toast.success("Excursion order updated");
  };

  const handleRecommendItinerary = async () => {
    if (!tourPublicId) {
      toast.error("Current Tour Public Id not found.");
      return;
    }
    if (!atraafRecommendations.length) {
      toast.error("No Atraaf recommendations found.");
      return;
    }
    try {
      setRecommendingItinerary(true);
      const assignments = new Map<
        string,
        {
          venuePublicId: string;
          venueDate: string;
          atraafPublicIds: string[];
        }
      >();

      atraafRecommendations.forEach((atraaf) => {
        atraaf.dates.forEach((date) => {
          const key = `${atraaf.venuePublicId}_${date.venueDate}`;
          const existing = assignments.get(key);
          if (existing) {
            if (!existing.atraafPublicIds.includes(atraaf.atraafPublicId)) {
              existing.atraafPublicIds.push(atraaf.atraafPublicId);
            }
          } else {
            assignments.set(key, {
              venuePublicId: atraaf.venuePublicId,
              venueDate: date.venueDate,
              atraafPublicIds: [atraaf.atraafPublicId],
            });
          }
        });
      });
      const assignmentGroups = Array.from(assignments.values());
      if (!assignmentGroups.length) {
        toast.info("All recommended Atraafs are already assigned.");
        return;
      }
      for (const assignment of assignmentGroups) {
        const response = await assignAtraafsBulk(tourPublicId, {
          venuePublicId: assignment.venuePublicId,
          venueDate: assignment.venueDate,
          atraafPublicIds: assignment.atraafPublicIds,
          overrideExisting: true,
        });
        if (!response.success) {
          throw new Error(
            response.message ||
              `Failed to assign Atraafs for ${assignment.venueDate}.`,
          );
        }
      }
      const assignedResponse = await getAssignedTourAtraaf(tourPublicId);
      if (assignedResponse.success) {
        setAssignedAtraaf(assignedResponse.data.assignments);
      }
      toast.success("Recommended Atraafs assigned successfully.");
    } catch (error) {
      console.error("Failed to recommend Atraafs:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to assign recommended Atraafs.",
      );
    } finally {
      setRecommendingItinerary(false);
    }
  };

  return (
    <DndContext
      sensors={isReadOnly ? undefined : sensors}
      collisionDetection={closestCenter}
      autoScroll={!isReadOnly}
      onDragEnd={isReadOnly ? undefined : handleGlobalDragEnd}
    >
      <div className="flex flex-col lg:flex-row gap-4 lg:h-[calc(100vh-240px)]">
        {/* Main Content Area */}
        <div className="w-full lg:w-[70%] space-y-4 min-h-0 pr-2 lg:overflow-y-auto">
          {/* Header with Recommend Button */}
          <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Select excursions
              </h2>
              {/* <p className="text-xs text-muted-foreground mt-1">
                REF: {tourPublicId || "IRQ-2026-0847"} · {selectedApplicants.length}{" "}
                pax · Pending Approval · Drag to reorder
              </p> */}
            </div>
            <Button
              onClick={handleRecommendItinerary}
              disabled={
                isReadOnly ||
                recommendingItinerary ||
                loadingItineraryRecommendations
              }
              className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {loadingItineraryRecommendations
                ? "Loading Recommendation..."
                : recommendingItinerary
                  ? "Recommending..."
                  : "Recommend Itinerary"}
            </Button>
          </div>

          {/* Info Banner */}
          <div
            className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6"
            style={{ marginTop: "0px" }}
          >
            <div className="flex items-start gap-2">
              <GripVertical className="w-4 h-4 text-green-700 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-green-900">
                Transport is generated from the Venue Summary using its
                buffer-calculated times. These mandatory entries cannot be
                removed. Recommend Itinerary adds suitable activities around
                them.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {itineraryDays.map((day, index) => {
              const activityDay = index + 1;
              const activities = excursionsByDay[activityDay] || [];
              const transports = mandatoryTransport.filter(
                (item) => item.date === day.date,
              );

              const dayCard = (
                <div
                  className={`min-h-[116px] rounded-lg bg-amber-50 p-4 transition-all ${
                    activityDay && dropTargetDay === activityDay
                      ? "ring-2 ring-blue-500 ring-offset-2"
                      : ""
                  }`}
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">
                        Day {index + 1} · {formatDate(day.date)}
                        {day.venueName ? ` · ${day.venueName}` : ""}
                      </h3>
                      {activityDay && (
                        <p className="mt-0.5 text-xs text-gray-600">
                          {formatDuration(
                            getDayContext(activityDay)?.remainingMinutes || 0,
                          )}{" "}
                          remaining
                          {" · "}drag to reorder
                        </p>
                      )}
                    </div>
                    {(transports.length > 0 || activities.length > 0) && (
                      <span className="text-xs font-semibold text-green-700">
                        {transports.length + activities.length} item
                        {transports.length + activities.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    {transports.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-start gap-3 rounded-md border border-blue-100 bg-white p-3"
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600">
                          <Truck className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {item.name}
                          </p>
                          <p className="mt-1 flex items-center gap-1 text-xs text-gray-600">
                            <Clock3 className="h-3 w-3" />
                            {item.time}
                          </p>
                        </div>
                        <LockKeyhole
                          className="h-4 w-4 shrink-0 text-blue-600"
                          aria-label="Required transport"
                        />
                      </div>
                    ))}

                    {activityDay && (
                      <SortableContext
                        items={activities.map((activity) => activity.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {activities.map((activity) => (
                          <SortableExcursionCard
                            key={activity.id}
                            excursion={activity}
                            isReadOnly={isReadOnly}
                            onRemove={async (activityId) => {
                              try {
                                const assigned = assignedAtraaf.find((a) => {
                                  const excursion = Object.values(
                                    excursionsByDay,
                                  )
                                    .flat()
                                    .find((item) => item.id === activityId);

                                  return (
                                    excursion?.masterId === a.atraafPublicId
                                  );
                                });

                                if (!assigned) {
                                  toast.error("Assignment not found.");
                                  return;
                                }

                                const response = await deleteAssignedTourAtraaf(
                                  assigned.publicId,
                                );

                                if (!response.success) {
                                  toast.error(response.message);
                                  return;
                                }

                                setExcursionsByDay((prev) => ({
                                  ...prev,
                                  [activityDay]: (
                                    prev[activityDay] || []
                                  ).filter((item) => item.id !== activityId),
                                }));

                                setAssignedAtraaf((prev) =>
                                  prev.filter(
                                    (a) => a.publicId !== assigned.publicId,
                                  ),
                                );

                                toast.success("Excursion removed");
                              } catch {
                                toast.error("Failed to remove excursion");
                              }
                            }}
                          />
                        ))}
                      </SortableContext>
                    )}
                  </div>
                </div>
              );

              return activityDay ? (
                <DayDropZone key={day.date} day={activityDay}>
                  {dayCard}
                </DayDropZone>
              ) : (
                <React.Fragment key={day.date}>{dayCard}</React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Right Sidebar - Available Atraafs & Activities */}
        <div className="w-full lg:w-[30%] shrink-0 min-h-0">
          <AvailableAtraafDropZone>
            <div className="bg-white border rounded-lg shadow-sm h-full flex flex-col">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-sm text-gray-900">
                  Available Itinerary Options
                </h3>
                <p className="text-xs text-gray-600 mt-1">
                  Atraaf routes and Mawaid
                </p>
              </div>

              <div className="lg:flex-1 lg:overflow-y-auto overscroll-contain">
                {/* Atraaf Master Section */}
                <div className="border-b">
                  <button
                    onClick={() =>
                      setSidebarCollapsed((prev: SidebarCollapsed) => ({
                        ...prev,
                        ziyarat: !prev.ziyarat,
                      }))
                    }
                    className="w-full px-4 py-3 flex flex-wrap items-center justify-between gap-2 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <MapPin className="w-4 h-4 text-purple-600" />
                      <span className="font-medium text-sm">Atraaf Routes</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {atraafOptions.length}
                      </Badge>
                      <ArrowRight
                        className={`w-4 h-4 transition-transform ${sidebarCollapsed.ziyarat ? "" : "rotate-90"}`}
                      />
                    </div>
                  </button>
                  {!sidebarCollapsed.ziyarat && (
                    <div className="px-2 pb-2 space-y-2">
                      {loadingAtraaf && (
                        <p className="py-4 text-center text-xs text-gray-500">
                          Loading Atraaf Master…
                        </p>
                      )}
                      {!loadingAtraaf && atraafOptions.length === 0 && (
                        <p className="py-4 text-center text-xs text-gray-500">
                          No active routes found in Atraaf Master
                        </p>
                      )}
                      {atraafOptions.map((item) => (
                        <ActivityDraggable
                          key={item.id}
                          item={item}
                          category="atraaf"
                          isReadOnly={isReadOnly}
                        >
                          <div className="rounded-lg border border-gray-200 bg-white p-3 transition-all hover:border-purple-400 hover:bg-purple-50/40">
                            <div className="flex items-start gap-2">
                              <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <h4 className="font-medium text-sm text-gray-900">
                                    {item.name}
                                  </h4>
                                  <Badge
                                    className={
                                      item.optionalRoute
                                        ? "bg-purple-100 text-purple-700 text-[10px]"
                                        : "bg-blue-100 text-blue-700 text-[10px]"
                                    }
                                  >
                                    {item.badge}
                                  </Badge>
                                </div>
                                <p className="mt-1 text-xs text-gray-600">
                                  {item.description}
                                </p>
                                {item.stops.length > 0 && (
                                  <p className="mt-1.5 text-xs leading-5 text-gray-500">
                                    {item.stops.join(" → ")}
                                  </p>
                                )}
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {item.transportRequired && (
                                    <Badge
                                      variant="outline"
                                      className="border-green-200 bg-green-50 text-[10px] text-green-700"
                                    >
                                      Transport included
                                    </Badge>
                                  )}
                                  <Badge
                                    variant="outline"
                                    className="text-[10px] text-gray-600"
                                  >
                                    {formatDuration(item.durationMinutes)} total
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </div>
                        </ActivityDraggable>
                      ))}
                      <p className="text-xs text-gray-500 text-center py-2">
                        ↑ drag onto a day
                      </p>
                    </div>
                  )}
                </div>

                {/* Mawaid Section */}
                <div>
                  <button
                    onClick={() =>
                      setSidebarCollapsed((prev: SidebarCollapsed) => ({
                        ...prev,
                        mawaid: !prev.mawaid,
                      }))
                    }
                    className="w-full px-4 py-3 flex flex-wrap items-center justify-between gap-2 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-base">🍽️</span>
                      <span className="font-medium text-sm">Mawaid</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant="outline"
                        className="text-[10px] text-gray-500"
                      >
                        Module
                      </Badge>
                      <ArrowRight
                        className={`w-4 h-4 transition-transform ${sidebarCollapsed.mawaid ? "" : "rotate-90"}`}
                      />
                    </div>
                  </button>
                  {!sidebarCollapsed.mawaid && (
                    <div className="px-3 pb-3">
                      <div className="rounded-lg border border-dashed border-orange-200 bg-orange-50/50 px-3 py-4 text-center">
                        <p className="text-xs font-medium text-gray-700">
                          Mawaid options will appear here
                        </p>
                        <p className="mt-1 text-[11px] text-gray-500">
                          Data will come from the new Mawaid module
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </AvailableAtraafDropZone>
        </div>
      </div>
    </DndContext>
  );
}
